import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { blogData, blogCategories } from '../data/mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogCard = ({ post, featured = false }) => {
  if (featured) {
    return (
      <article className="group">
        <Link to={`/blog/${post.slug}`} className="block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Image */}
            <div className="aspect-[16/10] overflow-hidden border border-[#2a2a2a] image-hover">
              <img 
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Content */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-xs font-medium uppercase tracking-wider text-[#0a0a0a] bg-[#c9a962] px-3 py-1">
                  {post.categoryLabel}
                </span>
                <span className="body-small text-[#808080]">{post.date}</span>
              </div>
              
              <h2 className="heading-1 text-white mb-4 group-hover:text-[#c9a962] transition-colors">
                {post.title}
              </h2>
              
              <p className="body-large text-[#b3b3b3] mb-6">
                {post.excerpt}
              </p>
              
              <div className="flex items-center gap-6">
                <span className="flex items-center gap-2 body-small text-[#808080]">
                  <Clock size={14} />
                  {post.readTime}
                </span>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-[#c9a962] group-hover:gap-3 transition-all">
                  Read Article
                  <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group">
      <Link to={`/blog/${post.slug}`} className="block">
        <div className="aspect-[16/10] overflow-hidden mb-4 border border-[#2a2a2a] image-hover">
          <img 
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-[#c9a962]">
            {post.categoryLabel}
          </span>
          <span className="body-small text-[#808080]">{post.date}</span>
        </div>
        
        <h3 className="heading-3 text-white mb-3 group-hover:text-[#c9a962] transition-colors">
          {post.title}
        </h3>
        
        <p className="body-regular text-[#b3b3b3] mb-4 line-clamp-2">
          {post.excerpt}
        </p>
        
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 body-small text-[#808080]">
            <Clock size={14} />
            {post.readTime}
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#c9a962] group-hover:gap-3 transition-all">
            Read More
            <ArrowRight size={16} />
          </span>
        </div>
      </Link>
    </article>
  );
};

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogData.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured || activeCategory !== 'all');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#0a0a0a]">
        <div className="container">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
              Blog & Insights
            </span>
            
            <h1 className="hero-large text-white mb-6">
              Insights & Content Tips for Growing Your Brand Online
            </h1>
            
            <p className="body-large text-[#b3b3b3]">
              Practical social media advice, trends, and behind-the-scenes knowledge from a content creator with 2+ years of experience
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-8 bg-[#111111] border-y border-[#2a2a2a] sticky top-16 md:top-20 z-40">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {blogCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-[#c9a962] text-[#0a0a0a]'
                      : 'bg-transparent text-[#b3b3b3] border border-[#2a2a2a] hover:border-[#c9a962] hover:text-[#c9a962]'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
            
            {/* Search */}
            <div className="relative w-full lg:w-72">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0a0a0a] border-[#2a2a2a] text-white rounded-none h-10 focus:border-[#c9a962] focus:ring-[#c9a962] placeholder:text-[#808080]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="body-large text-[#808080]">No articles found matching your criteria.</p>
              <Button 
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
                className="mt-4 bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && activeCategory === 'all' && !searchQuery && (
                <div className="mb-16 pb-16 border-b border-[#2a2a2a]">
                  <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-6">
                    Featured Article
                  </span>
                  <BlogCard post={featuredPost} featured />
                </div>
              )}
              
              {/* Post Count */}
              <div className="flex items-center justify-between mb-8">
                <p className="body-regular text-[#808080]">
                  {regularPosts.length} {regularPosts.length === 1 ? 'article' : 'articles'} {activeCategory !== 'all' && `in ${blogCategories.find(c => c.id === activeCategory)?.label}`}
                </p>
              </div>
              
              {/* Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 text-white mb-4">
              Want Content That Actually Converts?
            </h2>
            <p className="body-large text-[#b3b3b3] mb-8">
              Let's create scroll-stopping content for your brand. View my portfolio or get in touch to discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="bg-[#c9a962] text-[#0a0a0a] hover:bg-[#d4b978] border-none rounded-none px-8 py-4 h-auto text-sm font-medium transition-all duration-300"
              >
                <Link to="/#portfolio">View Portfolio</Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none px-8 py-4 h-auto text-sm font-medium transition-all duration-300"
              >
                <Link to="/#contact">Get in Touch</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
